import {
	linkTemplateLOTO,
	unLinkTemplateLOTO,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import type { GridApi } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useTemplateLOTOGridActionsAndButtons = ({
	newMode,
	gridRef,
	searchTemplateLOTO,
	setShowLinkResourcesToAssetDialog,
	setRemoveLOTOError,
	setListMessage,
	TemplateId,
}: {
	TemplateId: number | null;
	newMode?: boolean;
	gridRef: GridApi | null;
	searchTemplateLOTO: Function;
	setRemoveLOTOError: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog?: Function;
	setListMessage: (err: string) => void | null;
}) => {
	const {
		state: {
			rowSelectedInLOTO,
			templateLOTOList,
			LOTONumberToLink,
			LOTONameToLink,
		},
		actions: {
			setFirstTime,
			setIsCreatingLOTO,
			setTemplateLOTOList,
			setDataLOTOSelected,
			setTemplateLOTOToAdd,
			setLOTONumberToLink,
			setLOTONameToLink,
			setRowSelectedInLOTO,
			setListLOTOSelected,
		},
	} = useTemplatesContext();

	const [refreshTrigger, setRefreshTrigger] = useState(1);

	// BUTTONS STATES
	const [addButton, setAddButton] = useState<boolean>(true);
	const [deleteButton, setDeleteButton] = useState<boolean>(false);
	const [saveButton, setSaveButton] = useState<boolean>(false);
	const [discardButton, setDiscardButton] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const isMountRef = useRef(true);
	const [searchParams] = useSearchParams();

	const refresh = useCallback(() => {
		if (!isMountRef.current) {
			const updatedList = templateLOTOList.filter(
				(item: any) => item.id !== -1,
			);

			setTemplateLOTOList(updatedList);
			setFirstTime(true);
			setSaveButton(false);
			setDiscardButton(false);
			setAddButton(true);
			setDeleteButton(false);
			setDeleteOpen(false);
			setDataLOTOSelected(undefined);
			searchTemplateLOTO();
		}
	}, [searchParams, TemplateId]);

	const saveRow = useCallback(async () => {
		if (LOTONumberToLink === "" || LOTONameToLink === "") {
			setListMessage("LOTO number and LOTO name are required");
		} else {
			const newTemplateLOTO = {
				LOTONumber: LOTONumberToLink,
				LOTOName: LOTONameToLink,
				IsAssetResource: false,
			};
			setTemplateLOTOToAdd(newTemplateLOTO);
			const res = await linkTemplateLOTO(TemplateId as number, [
				newTemplateLOTO,
			]);
			if (res.ok) {
				setIsCreatingLOTO(false);
				if (setShowLinkResourcesToAssetDialog)
					setShowLinkResourcesToAssetDialog(true);
				setLOTONumberToLink("");
				setLOTONameToLink("");
				setRefreshTrigger((prev) => prev + 1);
			} else setListMessage(res.message);
		}
	}, [
		LOTONumberToLink,
		LOTONameToLink,
		TemplateId,
		linkTemplateLOTO,
		setRemoveLOTOError,
		setRefreshTrigger,
	]);

	const addRow = useCallback(() => {
		setIsCreatingLOTO(true);
		setAddButton(false);
		setSaveButton(true);
		setDiscardButton(true);
		setDeleteButton(false);
		const newData = { id: -1, TemplateId: TemplateId };
		const currentData = templateLOTOList ? templateLOTOList : [];
		const data = [newData, ...currentData];
		setTemplateLOTOList(data);
	}, [templateLOTOList, TemplateId]);

	const cancelAddLOTO = useCallback(() => {
		const updatedList = templateLOTOList.filter((item: any) => item.id !== -1);

		setIsCreatingLOTO(false);
		setAddButton(true);
		setSaveButton(false);
		setDiscardButton(false);
		setDeleteButton(false);
		setDataLOTOSelected(null);
		setLOTONumberToLink("");
		setLOTONameToLink("");
		setTemplateLOTOList(updatedList);
	}, [
		templateLOTOList,
		setTemplateLOTOList,
		setDataLOTOSelected,
		setIsCreatingLOTO,
	]);

	const removeLOTO = useCallback(
		async (LOTOToRemove?: any[]) => {
			if (LOTOToRemove && LOTOToRemove.length > 0) {
				const formattedLOTO = LOTOToRemove.map((loto: any) => ({
					LOTONumber: loto.data.LOTONumber,
					LOTOName: loto.data.LOTOName,
					IsAssetResource: loto.data.IsAssetResource,
				}));

				const res = await unLinkTemplateLOTO(
					TemplateId as number,
					formattedLOTO,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveLOTOError(res.message);
				}
			} else if (rowSelectedInLOTO) {
				const formattedLOTO = [
					{
						LOTONumber: rowSelectedInLOTO.LOTONumber,
						LOTOName: rowSelectedInLOTO.LOTOName,
						IsAssetResource: rowSelectedInLOTO.IsAssetResource,
					},
				];

				const res = await unLinkTemplateLOTO(
					TemplateId as number,
					formattedLOTO,
				);
				if (res.ok) {
					refresh();
				} else {
					setRemoveLOTOError(res.message);
				}
			}

			setRowSelectedInLOTO(null);
			setDataLOTOSelected(null);
			setDeleteOpen(false);
			setAddButton(true);
			setSaveButton(false);
			setDiscardButton(false);
			setDeleteButton(false);
			setListLOTOSelected([]);

			if (gridRef) {
				gridRef.deselectAll();
			}
		},
		[
			TemplateId,
			unLinkTemplateLOTO,
			refresh,
			setRemoveLOTOError,
			rowSelectedInLOTO,
			setRowSelectedInLOTO,
			setDataLOTOSelected,
			setDeleteOpen,
			setAddButton,
			setSaveButton,
			setDiscardButton,
			setDeleteButton,
			setListLOTOSelected,
			gridRef,
		],
	);

	useEffect(() => {
		refresh();
	}, [refreshTrigger]);

	useEffect(() => {
		isMountRef.current = false;
	}, []);

	return {
		refresh,
		saveRow,
		addRow,
		removeLOTO,
		cancelAddLOTO,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	};
};
